/**
 * Cross-field find & replace.
 *
 * FindReplaceDirective is per-textarea by construction, which is the right shape
 * for the common case. This adds the other one: a clinician who spots a misspelled
 * drug name in the note needs it fixed in the letters too, because those letters
 * were generated *from* the note and carry the same mistake.
 *
 * A scope is deliberately expressed as FormControls rather than as the rendered
 * textareas. Sections of the AI request are behind
 * `*ngIf="useDynamicLayout ? visibleSections.X : true"`, so a registry of live
 * directives would quietly miss whatever happens to be hidden — the one failure
 * mode that makes "replace everywhere" untrustworthy.
 */

import { AbstractControl } from '@angular/forms';
import { buildReplaceAllPatch, findMatches } from './find-replace.util';

/**
 * One replaceable field. Accessor-based so a scope can mix reactive-form controls
 * with the odd ngModel-bound property without either side knowing about the other.
 */
export interface FindReplaceTarget {
  /** Shown to the clinician in the confirmation dialog. */
  label: string;
  read(): string;
  write(value: string): void;
  /** Skipped entirely when true — never counted, never written. */
  readOnly?(): boolean;
}

/** How many matches a single target holds, and what it would become. */
export interface TargetHit {
  target: FindReplaceTarget;
  count: number;
}

export interface ScopeReplaceSummary {
  /** Total occurrences rewritten. */
  occurrences: number;
  /** Fields actually touched. */
  fields: number;
}

/** Wraps a reactive-form control as a target. Returns null for a missing control. */
export function controlTarget(label: string, control: AbstractControl | null): FindReplaceTarget | null {
  if (!control) {
    return null;
  }
  return {
    label,
    read: () => (control.value == null ? '' : String(control.value)),
    // emitEvent stays on: FindReplaceDirective re-highlights off valueChanges, and
    // the form has to go dirty or the edit won't survive a save.
    write: value => control.setValue(value),
    readOnly: () => control.disabled
  };
}

/** Drops the nulls left by `controlTarget` when a control name doesn't resolve. */
export function targets(...list: (FindReplaceTarget | null)[]): FindReplaceTarget[] {
  return list.filter((t): t is FindReplaceTarget => !!t);
}

/** Every target holding at least one match, in declaration order. */
export function scopeHits(list: FindReplaceTarget[], term: string): TargetHit[] {
  if (!term) {
    return [];
  }

  const hits: TargetHit[] = [];
  for (const target of list) {
    if (target.readOnly?.()) {
      continue;
    }
    const count = findMatches(target.read(), term).length;
    if (count) {
      hits.push({ target, count });
    }
  }
  return hits;
}

export function totalMatches(hits: TargetHit[]): number {
  return hits.reduce((sum, hit) => sum + hit.count, 0);
}

/** One field's before/after, kept so a scope replace rolls back as a single step. */
export interface ScopeUndoEntry {
  target: FindReplaceTarget;
  before: string;
  after: string;
}

/**
 * Rewrites every match of `term` across `list`. Matching is redone here rather
 * than trusting the counts gathered for the dialog: confirmation is async, and an
 * AI draft can land in a control while the dialog is open.
 *
 * Returns the undo entries alongside the summary so the caller can offer a single
 * rollback across every field it touched.
 */
export function applyScopeReplace(
  list: FindReplaceTarget[],
  term: string,
  replacement: string
): { summary: ScopeReplaceSummary; undo: ScopeUndoEntry[] } {
  const undo: ScopeUndoEntry[] = [];
  let occurrences = 0;

  if (term) {
    for (const target of list) {
      if (target.readOnly?.()) {
        continue;
      }

      const before = target.read();
      const matches = findMatches(before, term);
      if (!matches.length) {
        continue;
      }

      const patch = buildReplaceAllPatch(before, matches, replacement);
      if (!patch) {
        continue;
      }

      const after = before.slice(0, patch.start) + patch.text + before.slice(patch.end);
      target.write(after);
      undo.push({ target, before, after });
      occurrences += matches.length;
    }
  }

  return { summary: { occurrences, fields: undo.length }, undo };
}

/**
 * Rolls a scope replace back, but only where the field still reads exactly as we
 * left it. Anything the clinician has since edited is left alone — silently
 * discarding their typing would be worse than a partial undo.
 *
 * @returns how many fields were actually restored.
 */
export function revertScopeReplace(entries: ScopeUndoEntry[]): number {
  let restored = 0;
  for (const entry of entries) {
    if (entry.target.readOnly?.() || entry.target.read() !== entry.after) {
      continue;
    }
    entry.target.write(entry.before);
    restored++;
  }
  return restored;
}

/** True while every touched field still holds the text this module wrote. */
export function canRevertScopeReplace(entries: ScopeUndoEntry[]): boolean {
  return (
    entries.length > 0 &&
    entries.every(entry => !entry.target.readOnly?.() && entry.target.read() === entry.after)
  );
}
